import { useEffect, useState, useRef, useCallback, RefCallback } from 'react';
import { VisibilityObserverOptions } from '@box-types';

export const useVisibilityObserver = ({
  root = null,
  rootMargin = '0px 0px 0px 0px',
  threshold = [0],
  triggerOnce = false,
  onEnteredViewport,
  onLeavingViewport
}: VisibilityObserverOptions): RefCallback<HTMLElement> => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const isElementVisibleRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Cleanup previous observer when dependencies change
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const refCallback = useCallback<RefCallback<HTMLElement>>((node) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;
    if (observerRef?.current) return;

    const options: IntersectionObserverInit = {
      root,
      rootMargin,
      threshold
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onEnteredViewport?.(entry);
          isElementVisibleRef.current = true;

          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else {
          if (isElementVisibleRef.current) {
            onLeavingViewport?.(entry);
            isElementVisibleRef.current = false;
          }
        }
      });
    }, options);

    observer.observe(element);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [element, root, rootMargin, threshold, triggerOnce, onEnteredViewport, onLeavingViewport]);

  return refCallback;
};
